/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useAppConfig } from '@airasia-rn/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { Platform } from 'react-native';
import axios from 'axios';
import type { AccessTokenResponse, RegisteredPasskey } from '../managers/sso';
import {
  accountKeys,
  getRefreshToken,
  handleLoginError,
  handleSuccessfulLogin,
  useAccessToken,
  useApiKey,
  useApiQuery,
  useRecaptchaManager,
  useSsoApiService,
  useUser,
} from './account';
import { createPasskeyManager } from '../managers';
import { RECAPTCHA_ACTIONS } from '../constants/recaptcha';
import { isSocialAuthCancelled } from '../utils/socialSignIn';
import { getUserId as getUserIdFromJwt } from '../utils/jwtUtils';

import { describeError } from '../utils/describeError';
/** Passkey list row with a resolved current-device flag for UI. */
export type RegisteredPasskeyItem = RegisteredPasskey & {
  isThisDevice: boolean;
};

/**
 * Resolves whether a passkey belongs to the current device.
 *
 * Prefers the backend `isThisDevice` flag when present; otherwise compares
 * stored `deviceId` with the app's current device identifier.
 */
export const resolvePasskeyIsThisDevice = (
  passkey: RegisteredPasskey,
  currentDeviceId?: string,
): boolean => {
  if (typeof passkey.isThisDevice === 'boolean') {
    return passkey.isThisDevice;
  }
  if (!currentDeviceId || !passkey.deviceId) {
    return false;
  }
  return passkey.deviceId === currentDeviceId;
};

export const mapRegisteredPasskeyToItem = (
  passkey: RegisteredPasskey,
  currentDeviceId?: string,
): RegisteredPasskeyItem => ({
  ...passkey,
  isThisDevice: resolvePasskeyIsThisDevice(passkey, currentDeviceId),
});

export const mapRegisteredPasskeys = (
  passkeys: RegisteredPasskey[],
  currentDeviceId?: string,
): RegisteredPasskeyItem[] =>
  passkeys.map((passkey) =>
    mapRegisteredPasskeyToItem(passkey, currentDeviceId),
  );

/** Whether the current device has a registered passkey (Android `userHasDevicePasskey`). */
export const userHasDevicePasskey = (
  passkeys: RegisteredPasskeyItem[],
): boolean => passkeys.some((passkey) => passkey.isThisDevice);

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const PASSKEY_RETRYABLE_ERROR_CODE = '1004';

/** Matches Android ClientException code when RP ID / domain is not ready yet — retry once after delay. */
const withPasskeyRetry = async <T>(operation: () => Promise<T>) => {
  try {
    return await operation();
  } catch (error: unknown) {
    const code =
      error != null && typeof error === 'object' && 'code' in error
        ? String((error as { code?: string | number }).code)
        : '';
    if (code === PASSKEY_RETRYABLE_ERROR_CODE) {
      await delay(3000);
      return await operation();
    }
    throw error;
  }
};

/**
 * Hook for logging in with passkey.
 *
 * - Fetches passkey options from SSO
 * - Delegates passkey authentication to the platform passkey manager
 * - Verifies passkey response and stores auth tokens
 */
export const useLoginByPasskey = () => {
  const api = useSsoApiService();
  const queryClient = useQueryClient();
  const recaptchaManager = useRecaptchaManager();
  const appConfig = useAppConfig();
  const apiKeyQuery = useApiKey();

  return useMutation({
    mutationFn: async ({
      userName,
      status,
    }: {
      userName?: string;
      status?: string;
    }) => {
      const clientKey = await apiKeyQuery.getToken();
      const gaClientId = appConfig.config.gaId;
      if (!clientKey) throw new Error('Missing API keys');

      const options = await api.getAuthenticatorOptions(
        userName ?? null,
        clientKey.id || '',
        clientKey.apiKey || '',
        await recaptchaManager.execute(RECAPTCHA_ACTIONS.LOGIN),
        gaClientId,
      );

      if (!options.options) {
        throw new Error('Missing passkey options');
      }

      const manager = createPasskeyManager();
      const authenticate = await withPasskeyRetry(() =>
        manager.authenticate(options.options!),
      );

      const tokenResponse = (await api.authenticatorVerification(
        {
          verificationAuthenticationResponse: {
            ...authenticate,
            userId: options.userId,
          },
          updateForm: options.updateForm,
          loyaltyId: options.loyaltyId,
          status,
        },
        clientKey.id || '',
        clientKey.apiKey || '',
        await recaptchaManager.execute(RECAPTCHA_ACTIONS.LOGIN),
        gaClientId,
        Platform.OS,
      )) as AccessTokenResponse;

      await handleSuccessfulLogin(queryClient, tokenResponse);
      return tokenResponse;
    },
    onError: async (error) => {
      // Cancelled Face ID / passkey sheet: LoginView shows oops via the flow
      // catch; skip handleLoginError so DEV LogBox doesn't cover that banner.
      if (isSocialAuthCancelled(error)) {
        return;
      }
      await handleLoginError(queryClient, error, 'Passkey');
    },
  });
};

/**
 * Lists passkeys registered for the current user (GET /auth/v2/authenticator).
 * Sends `X-Refresh-Token` when present, matching web clients.
 */
export const useRegisteredPasskeys = (caller: string) => {
  const api = useSsoApiService();
  const queryClient = useQueryClient();
  const apiKeyQuery = useApiKey();
  const accessTokenQuery = useAccessToken(caller);
  const appConfig = useAppConfig();
  const currentDeviceId = appConfig.config.deviceId;

  const queryResult = useApiQuery<RegisteredPasskey[], Error>({
    queryKey: accountKeys.registeredPasskeys,
    staleTime: 60 * 1000,
    queryFn: async () => {
      try {
        if (apiKeyQuery.error) {
          console.error(
            'Error fetching API - ',
            caller,
            ':',
            describeError(apiKeyQuery.error),
          );
        }
        if (accessTokenQuery.error) {
          console.error(
            'Error fetching access token - ',
            caller,
            ':',
            describeError(accessTokenQuery.error),
          );
        }

        const [clientKey, accessToken, refreshToken] = await Promise.all([
          apiKeyQuery.getToken(),
          accessTokenQuery.getToken(),
          getRefreshToken(queryClient),
        ]);
        if (!clientKey) throw new Error('Missing API keys');
        if (!accessToken) throw new Error('Missing access token');

        return await api.getRegisteredPasskey(
          clientKey.id || '',
          clientKey.apiKey || '',
          accessToken,
          appConfig.config.gaId,
          refreshToken || undefined,
        );
      } catch (error) {
        // Some environments may not expose the registered passkeys endpoint yet.
        // In that case, we show an empty state instead of breaking the screen.
        if (axios.isAxiosError(error) && error.response?.status === 404) {
          return [];
        }
        throw error;
      }
    },
    enabled: apiKeyQuery.isTokenValid && accessTokenQuery.isTokenValid,
  });

  const items = useMemo(
    () =>
      queryResult.data
        ? mapRegisteredPasskeys(queryResult.data, currentDeviceId)
        : undefined,
    [queryResult.data, currentDeviceId],
  );

  return {
    ...queryResult,
    items,
  };
};

/**
 * Register a new passkey for the logged-in user (same flow as Android `registerNewPasskey`).
 *
 * 1. `getAuthenticatorRegistrationOptions` — GET `/auth/v2/authenticator/registration/options/{username}` with `X-Refresh-Token` when available
 * 2. `createPasskeyManager().create(options)` — one retry after 3s when native error `code === "1004"` (RP / domain not ready)
 * 3. `authenticatorRegistrationVerification` — POST `/auth/v2/authenticator/registration/verification` with credential + `userId`
 *
 * Consumed by `PasskeyPage` via `useRegisterPasskey` → `mutate()`.
 */
export const useRegisterPasskey = () => {
  const api = useSsoApiService();
  const queryClient = useQueryClient();
  const accessTokenQuery = useAccessToken('registerPasskey');
  const apiKeyQuery = useApiKey();
  const userQuery = useUser('registerPasskey');
  const appConfig = useAppConfig();

  return useMutation({
    mutationFn: async () => {
      if (apiKeyQuery.error) {
        console.error(
          'Error fetching API - registerPasskey:',
          describeError(apiKeyQuery.error),
        );
      }
      if (accessTokenQuery.error) {
        console.error(
          'Error fetching access token - registerPasskey:',
          describeError(accessTokenQuery.error),
        );
      }

      const [clientKey, accessToken, refreshToken] = await Promise.all([
        apiKeyQuery.getToken(),
        accessTokenQuery.getToken(),
        getRefreshToken(queryClient),
      ]);
      const gaClientId = appConfig.config.gaId;

      if (!clientKey) throw new Error('Missing API keys');
      if (!accessToken) throw new Error('Missing access token');
      if (!userQuery.data?.username) throw new Error('Missing username');

      const options = await api.getAuthenticatorRegistrationOptions(
        userQuery.data.username,
        clientKey.id,
        clientKey.apiKey || '',
        accessToken,
        gaClientId,
        refreshToken || undefined,
      );

      const manager = createPasskeyManager();
      const passKeyRegister = await withPasskeyRetry(() =>
        manager.create(options),
      );

      await api.authenticatorRegistrationVerification(
        {
          ...passKeyRegister,
          userId: userQuery.data.id,
          deviceId: appConfig.config.deviceId || undefined,
        },
        {
          clientId: clientKey.id,
          apiKey: clientKey.apiKey || '',
          accessToken,
          gaId: gaClientId,
          refreshToken,
          /** Chrome omits platform on web CORS POST; native still sends User-Agent-equivalent tag. */
          clientPlatform: Platform.OS === 'web' ? undefined : Platform.OS,
        },
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: accountKeys.registeredPasskeys,
      });
    },
  });
};

/**
 * Removes a passkey after WebAuthn user verification (challenge + DELETE).
 */
export const useDeletePasskey = () => {
  const api = useSsoApiService();
  const queryClient = useQueryClient();
  const accessTokenQuery = useAccessToken('deletePasskey');
  const apiKeyQuery = useApiKey();
  const appConfig = useAppConfig();

  return useMutation({
    mutationFn: async (passkeyId: string) => {
      if (apiKeyQuery.error) {
        console.error(
          'Error fetching API - deletePasskey:',
          describeError(apiKeyQuery.error),
        );
      }
      if (accessTokenQuery.error) {
        console.error(
          'Error fetching access token - deletePasskey:',
          describeError(accessTokenQuery.error),
        );
      }

      const [clientKey, accessToken, refreshToken] = await Promise.all([
        apiKeyQuery.getToken(),
        accessTokenQuery.getToken(),
        getRefreshToken(queryClient),
      ]);
      const gaClientId = appConfig.config.gaId;

      if (!clientKey) throw new Error('Missing API keys');
      if (!accessToken) throw new Error('Missing access token');

      const userId = getUserIdFromJwt(accessToken);
      if (!userId) throw new Error('Missing user id');

      const options = await api.getAuthenticatorVerificationOptions(
        clientKey.id || '',
        clientKey.apiKey || '',
        undefined,
        accessToken,
        gaClientId,
      );

      const manager = createPasskeyManager();
      const authenticate = await withPasskeyRetry(() =>
        manager.authenticate(options),
      );

      const { authenticatorVerificationToken } =
        await api.authenticatorManagementVerification(
          {
            ...authenticate,
            clientExtensionResults: authenticate.clientExtensionResults ?? {},
            userId,
            purpose: 'deleteAuthenticator',
          },
          clientKey.id || '',
          clientKey.apiKey || '',
          accessToken,
          gaClientId,
          refreshToken,
          Platform.OS,
        );

      await api.deletePasskey(
        passkeyId,
        clientKey.id || '',
        clientKey.apiKey || '',
        accessToken,
        authenticatorVerificationToken,
        gaClientId,
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: accountKeys.registeredPasskeys,
      });
    },
  });
};
